import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { AlertTriangle, Search, FileText, Calendar, Hash } from "lucide-react";
import { PageShell } from "../components/layout/PageShell";
import { DataTable, ViewModeToggle, type Column } from "../components/DataTable";
import { Dropdown } from "../components/Dropdown";
import {
  MetaDates,
  StatTile,
  StatTiles,
  TitleTooltip,
  statusAccent,
} from "../components/EntityCardGrid";
import {
  safeFormatDate,
  getPageSizeOptions,
  resolvePageSizeForViewMode,
} from "../../utils/formatters";
import { listPoams, ApiPoam } from "../../api/frameworksApi";

export function POAM() {
  const navigate = useNavigate();

  const [poams, setPoams] = useState<ApiPoam[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [viewMode, setViewMode] = useState<"card" | "table">("card");
  // Must be a valid option for the default view mode above ("card") — the
  // plain table default (10) isn't one of [12,24,48,96].
  const [pageSize, setPageSize] = useState(getPageSizeOptions("card")[0]);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const handleSort = (key: string) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(0);
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listPoams();
      setPoams(res?.data ?? []);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load POA&M data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filtered = poams.filter((p) => {
    const count = p.itemCount ?? p.items?.length ?? 0;
    if (count === 0) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (p.sspName ?? "").toLowerCase().includes(q) ||
      (p.title ?? "").toLowerCase().includes(q) ||
      p.documentId.toLowerCase().includes(q)
    );
  });

  const SortIcon = ({ col }: { col: string }) => {
    const active = sortKey === col;
    return (
      <span
        className="inline-flex flex-col items-center ml-2 select-none"
        style={{ fontSize: "7px", lineHeight: "7px" }}
      >
        <span
          className={`font-black ${active && sortDir === "asc" ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}
        >
          ▲
        </span>
        <span
          className={`font-black ${active && sortDir === "desc" ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}
        >
          ▼
        </span>
      </span>
    );
  };

  const sorted = [...filtered].sort((a, b) => {
    if (!sortKey) return 0;
    let aVal: any = "";
    let bVal: any = "";
    if (sortKey === "sspName") {
      aVal = a.sspName ?? "";
      bVal = b.sspName ?? "";
    } else if (sortKey === "title") {
      aVal = a.title ?? a.documentId;
      bVal = b.title ?? b.documentId;
    } else if (sortKey === "itemCount") {
      aVal = a.itemCount ?? a.items?.length ?? 0;
      bVal = b.itemCount ?? b.items?.length ?? 0;
    } else if (sortKey === "createdAt") {
      aVal = new Date(a.createdAt).getTime();
      bVal = new Date(b.createdAt).getTime();
    } else if (sortKey === "updatedAt") {
      aVal = new Date(a.updatedAt || a.createdAt).getTime();
      bVal = new Date(b.updatedAt || b.createdAt).getTime();
    } else {
      return 0;
    }
    if (typeof aVal === "number")
      return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    return sortDir === "asc"
      ? aVal.localeCompare(bVal as string)
      : (bVal as string).localeCompare(aVal);
  });

  const paged = sorted.slice(page * pageSize, (page + 1) * pageSize);

  const columns: Column<ApiPoam>[] = [
    {
      key: "no",
      header: "#",
      align: "center",
      render: (_row, idx) => (
        <span className="text-[#0f172a] dark:text-white/80 font-medium">
          {page * pageSize + idx + 1}
        </span>
      ),
    },
    {
      key: "sspName",
      align: "left",
      ellipsis: 2,
      tooltip: (p) =>
        p.sspName || p.importSspHref?.replace(/^urn:uuid:/, "") || "",
      header: (
        <button
          onClick={() => handleSort("sspName")}
          className="flex items-center mx-auto font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
        >
          SSP Name
          <SortIcon col="sspName" />
        </button>
      ),
      render: (p) => (
        <p className="font-medium text-[#0f172a] dark:text-white/80">
          {p.sspName ||
            p.importSspHref?.replace(/^urn:uuid:/, "").slice(0, 12) ||
            "-"}
        </p>
      ),
    },
    {
      key: "title",
      align: "left",
      ellipsis: 2,
      tooltip: (p) => p.title || p.documentId || "",
      header: (
        <button
          onClick={() => handleSort("title")}
          className="flex items-center mx-auto font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
        >
          POA&amp;M Title
          <SortIcon col="title" />
        </button>
      ),
      render: (p) => (
        <div className="flex items-center gap-2">
          <FileText className="size-3.5 text-[#94a3b8] dark:text-white/55 flex-shrink-0" />
          <p className="text-sm font-medium text-[#0f172a] dark:text-white/80">
            {p.title || p.documentId}
          </p>
        </div>
      ),
    },
    {
      key: "itemCount",
      align: "center",
      header: (
        <button
          onClick={() => handleSort("itemCount")}
          className="flex items-center mx-auto font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
        >
          Findings
          <SortIcon col="itemCount" />
        </button>
      ),
      render: (p) => (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-bold bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400">
          <Hash className="size-3" />
          {p.itemCount ?? p.items?.length ?? 0}
        </span>
      ),
    },
    {
      key: "createdAt",
      align: "center",
      header: (
        <button
          onClick={() => handleSort("createdAt")}
          className="flex items-center mx-auto font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
        >
          Created On
          <SortIcon col="createdAt" />
        </button>
      ),
      render: (p) => (
        <div className="inline-flex items-center gap-1.5 text-xs font-medium text-[#0f172a] dark:text-white/80 whitespace-nowrap">
          <Calendar className="size-3 text-[#94a3b8] dark:text-white/55" />
          {new Date(p.createdAt).toLocaleDateString("en-GB")}
        </div>
      ),
    },
    {
      key: "updatedAt",
      align: "center",
      header: (
        <button
          onClick={() => handleSort("updatedAt")}
          className="flex items-center mx-auto font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
        >
          Updated On
          <SortIcon col="updatedAt" />
        </button>
      ),
      render: (p) => (
        <div className="inline-flex items-center gap-1.5 text-xs font-medium text-[#0f172a] dark:text-white/80 whitespace-nowrap">
          <Calendar className="size-3 text-[#94a3b8] dark:text-white/55" />
          {new Date(p.updatedAt || p.createdAt).toLocaleDateString("en-GB")}
        </div>
      ),
    },
  ];

  return (
    <PageShell className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1>POA&amp;M</h1>
          <p className="text-[14px] text-[#94a3b8] dark:text-white/70">
            View and manage POA&M records for linked system security plans
          </p>
        </div>
      </div>

      {/* Search + Rows per page */}
      {!loading && !error && (
        <div className="flex items-center gap-3">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[#64748b] dark:text-white/50" />
            <input
              type="text"
              placeholder="Search by SSP or Title"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              className="w-full pl-9 pr-4 py-2 text-sm bg-white dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.07] rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-500/50 transition-all placeholder:text-[#94a3b8] dark:placeholder:text-white/60 text-[#0f172a] dark:text-white"
            />
          </div>
          <div className="flex items-center gap-3 ml-auto flex-shrink-0">
            <ViewModeToggle
              value={viewMode}
              onChange={(mode) => {
                setViewMode(mode);
                setPageSize(resolvePageSizeForViewMode(pageSize, mode));
                setPage(0);
              }}
            />
            <label className="text-sm d whitespace-nowrap">Rows per page</label>
            <Dropdown
              value={String(pageSize)}
              onChange={(v) => {
                setPageSize(Number(v));
                setPage(0);
              }}
              options={getPageSizeOptions(viewMode).map((n) => ({
                value: String(n),
                label: String(n),
              }))}
              size="sm"
              align="end"
              className="!min-w-[80px]"
            />
          </div>
        </div>
      )}

      {/* Table */}
      <DataTable<ApiPoam>
        columns={columns}
        data={paged}
        loading={loading}
        error={error}
        onRetry={fetchData}
        emptyText={
          search
            ? "No POA&M records match your search"
            : "No POA&M records available"
        }
        emptyIcon={
          <AlertTriangle className="size-5 text-[#cbd5e1] dark:text-white/15" />
        }
        onRowClick={(p) => navigate(`/poam/${p.id}`)}
        rowKey={(p) => p.id}
        page={page}
        pageSize={pageSize}
        totalItems={sorted.length}
        onPageChange={setPage}
        itemLabel="records"
        enableCardView
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        renderCard={(rows) => (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 pt-2 px-1">
            {rows.map((p) => {
              const accent = statusAccent("draft");
              const title = p.title || p.documentId || "Untitled POA&M";
              const sspName =
                p.sspName ||
                p.importSspHref?.replace(/^urn:uuid:/, "").slice(0, 12) ||
                null;
              const findings = p.itemCount ?? p.items?.length ?? 0;
              return (
                <div
                  key={p.id}
                  onClick={() => navigate(`/poam/${p.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(`/poam/${p.id}`);
                    }
                  }}
                  className={`group relative flex h-full flex-col rounded-2xl border-2 border-[#e8edf2] dark:border-white/20 bg-indigo-100/40 dark:bg-indigo-500/10 overflow-hidden transition-all duration-300 hover:-translate-y-1 ${accent.hoverBorder} ${accent.hoverGlow} cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40`}
                >
                  <span
                    aria-hidden
                    className={`pointer-events-none absolute inset-y-0 left-0 w-1/2 -translate-x-full skew-x-12 bg-gradient-to-r from-transparent ${accent.sheenVia} to-transparent group-hover:translate-x-[220%] group-hover:transition-transform group-hover:duration-[900ms] group-hover:ease-out`}
                  />
                  <div className="@container relative z-[1] flex flex-1 flex-col gap-3 p-3.5">
                    <div className="flex items-start justify-between gap-2.5">
                      <div className="flex-1 min-w-0">
                        <TitleTooltip text={title}>
                          <h3 className="text-[13px] font-bold leading-snug tracking-[-0.01em] text-foreground truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                            {title}
                          </h3>
                        </TitleTooltip>
                      </div>
                      <div className="shrink-0">
                        <FileText className="size-4 text-indigo-500 dark:text-indigo-400" />
                      </div>
                    </div>
                    {sspName && (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="inline-flex items-center gap-1 whitespace-nowrap max-w-full truncate px-1.5 py-0.5 rounded-md text-[10px] font-medium border bg-indigo-50 text-indigo-700 border-indigo-200/70 dark:bg-indigo-500/10 dark:text-indigo-300 dark:border-indigo-500/20">
                          <FileText className="size-3 flex-shrink-0" />
                          <span className="truncate">{sspName}</span>
                        </span>
                      </div>
                    )}
                    <div className="mt-auto flex flex-col gap-3">
                      <StatTiles>
                        <StatTile
                          icon={<Hash className="size-3.5" />}
                          accent="indigo"
                          value={findings}
                          label="Findings"
                        />
                        <StatTile
                          icon={<AlertTriangle className="size-3.5" />}
                          accent="emerald"
                          value={p.items?.length ?? 0}
                          label="Items"
                        />
                      </StatTiles>
                      <MetaDates
                        created={safeFormatDate(p.createdAt)}
                        updated={safeFormatDate(p.updatedAt || p.createdAt)}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      />
    </PageShell>
  );
}
